import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CreditCard, Send, Mail, TrendingUp, Clock, ArrowRight, Zap, BarChart3,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, AreaChart, Area } from "recharts";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, Spinner, Button } from "@/components/kit";

function Stat({ icon: Icon, label, value, color = "sky", sub }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl bg-${color}-50 grid place-items-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
      <p className="font-display text-2xl font-extrabold mt-3">{value}</p>
      <p className="text-sm text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

function rupiah(n) {
  if (!n) return "Rp0";
  return "Rp" + Number(n).toLocaleString("id-ID");
}

export default function UsageDashboard() {
  const { user, quota } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/usage/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => console.error("Usage load failed", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>;
  }

  const credits = data?.credits || {};
  const blast = data?.blast_summary || {};
  const monthly = data?.monthly_usage || [];
  const daily = data?.daily_activity || [];
  const recentJobs = data?.recent_jobs || [];
  const plan = data?.plan || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="inline-flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-sky-100 grid place-items-center shrink-0">
              <BarChart3 className="w-5 h-5 text-sky-600" />
            </span>
            <h1 className="font-display text-3xl font-extrabold">Dashboard Pemakaian</h1>
          </span>
          <p className="text-slate-500 mt-1">Pantau kredit, blast activity, dan history pemakaian kamu.</p>
        </div>
        <Link to="/app/billing">
          <Button variant="soft" size="sm"><CreditCard className="w-4 h-4" /> Upgrade / Beli Kredit</Button>
        </Link>
      </div>

      {/* Credit Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          icon={Zap}
          label="Kredit Tersedia"
          value={credits.available || 0}
          color="sky"
          sub={`${credits.monthly_remaining || 0} bulanan + ${credits.payg_balance || 0} PAYG`}
        />
        <Stat
          icon={TrendingUp}
          label="Kredit Terpakai (Bulan Ini)"
          value={credits.credits_used || 0}
          color="violet"
          sub={`dari ${credits.monthly_credits || 0} kuota bulanan`}
        />
        <Stat
          icon={Send}
          label="Pesan Terkirim"
          value={blast.total_sent || 0}
          color="green"
          sub={`WA: ${blast.wa_sent || 0} · Email: ${blast.email_sent || 0}`}
        />
        <Stat
          icon={Clock}
          label="Total Jobs Blast"
          value={blast.total_jobs || 0}
          color="amber"
          sub={`${blast.total_failed || 0} gagal`}
        />
      </div>

      {/* Credit Progress */}
      <Card className="p-6">
        <h3 className="font-display font-bold text-lg mb-4">Penggunaan Kredit Bulanan</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Kredit terpakai</span>
            <span className="font-semibold">{credits.credits_used || 0} / {credits.monthly_credits || 0}</span>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-400 to-sky-600 transition-all duration-500"
              style={{ width: `${credits.monthly_credits ? Math.min((credits.credits_used / credits.monthly_credits) * 100, 100) : 0}%` }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <p className="text-slate-500">Sisa harian</p>
              <p className="font-semibold">{credits.daily_remaining || 0} / {credits.daily_cap || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-lg px-3 py-2">
              <p className="text-slate-500">Kredit PAYG</p>
              <p className="font-semibold">{credits.payg_balance || 0}</p>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Usage Chart */}
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Penggunaan 6 Bulan</h3>
          {monthly.some((m) => m.credits_used > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly}>
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => [`${v} kredit`, "Terpakai"]} />
                <Bar dataKey="credits_used" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">Belum ada penggunaan bulanan.</p>
          )}
        </Card>

        {/* Daily Activity Chart */}
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Aktivitas 14 Hari</h3>
          {daily.some((d) => d.sent > 0 || d.failed > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={daily}>
                <defs>
                  <linearGradient id="ug" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="sent" stroke="#10b981" strokeWidth={2} fill="url(#ug)" name="Terkirim" />
                <Area type="monotone" dataKey="failed" stroke="#f43f5e" strokeWidth={2} fill="none" name="Gagal" strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">Belum ada aktivitas blast 14 hari terakhir.</p>
          )}
        </Card>
      </div>

      {/* Recent Jobs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Blast Terakhir</h3>
          <Link to="/app/blast-history" className="text-sm text-sky-600 hover:text-sky-700 font-semibold flex items-center gap-1">
            Lihat Semua <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {recentJobs.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Belum ada riwayat blast.</p>
        ) : (
          <div className="space-y-2">
            {recentJobs.map((j) => {
              const colors = {
                done: "green", running: "sky", queued: "amber", scheduled: "violet",
                paused: "amber", failed: "red", cancelled: "slate",
              };
              const labels = {
                done: "Selesai", running: "Berjalan", queued: "Menunggu",
                scheduled: "Terjadwal", paused: "Dijeda", failed: "Gagal", cancelled: "Dibatalkan",
              };
              return (
                <div key={j.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center ${j.blast_type === "email" ? "bg-violet-50" : "bg-emerald-50"}`}>
                    {j.blast_type === "email" ? <Mail className="w-4 h-4 text-violet-600" /> : <Send className="w-4 h-4 text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{j.template_name}</p>
                    <p className="text-xs text-slate-400">
                      {j.sent}/{j.total_leads} terkirim · {new Date(j.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                  <Badge color={colors[j.status] || "slate"}>{labels[j.status] || j.status}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent Payments */}
      {data?.recent_payments?.length > 0 && (
        <Card className="p-6">
          <h3 className="font-display font-bold text-lg mb-4">Pembayaran Terakhir</h3>
          <div className="space-y-2">
            {data.recent_payments.map((p) => (
              <div key={p.order_id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition text-sm">
                <div>
                  <p className="font-semibold text-slate-800">{p.type === "subscription" ? `Plan ${p.plan_id}` : p.type === "credit_pack" ? "Paket Kredit" : "Add-on"}</p>
                  <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{rupiah(p.gross_amount)}</p>
                  <Badge color={p.paid ? "green" : "amber"}>{p.paid ? "Lunas" : "Pending"}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
